import { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import { Flex } from 'antd';
import type { InputRef } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';
import LazySvg from 'components/LazySvg';
import Input from 'components/Input';
import Select from 'components/Select';
import InputMask from 'components/InputMask';
import DatePicker from 'components/DatePicker';
import Password from 'components/Password';
import AutoComplete from 'components/AutoComplete';
import If from 'components/If';
import {
  Optional,
  InputProps,
  SelectProps,
  SelectOptionType,
  MaskedInputProps,
  DatePickerProps,
  PasswordProps,
  AutoCompleteProps,
  LooseObjectType,
} from 'utils/globalTypes';
import { SUFFIX_OPTIONS } from 'utils/constants';
import { QaDataCollectionModal } from 'testdata/constants';
import { useAppDispatch } from 'app/hooks';
import { useFormikContext } from 'formik';
import { fetchUSStates, searchSmartyAddress } from 'services/misc/miscAsync';
import { buildAddress, logErrors } from 'utils';
import { debounce } from 'lodash';
import piiFormModel from './formModel';
import { OnSelectHandler, SmartyAddressType } from './type';
import homeAddressFormInitialValues from '../HomeAddress/FormModel/formInitialValues';

type PiiInputProps = Optional<InputProps, 'name'>;
type PiiSelectProps = Optional<SelectProps, 'name'>;
type PiiMaskedInputProps = Optional<MaskedInputProps, 'name' | 'mask'>;
type PiiDatePickerProps = Optional<DatePickerProps, 'name'>;
type PiiPasswordProps = Optional<PasswordProps, 'name'>;
type PiiAutoCompleteProps = Optional<AutoCompleteProps, 'name'>;

const {
  nameModel,
  addressModel,
  birthDateModel,
  phoneNumberModel,
  ssnModel,
  emailAddressModel,
} = piiFormModel;

const {
  groupId: nameGroupId,
  formFields: { first, middle, last, suffix },
} = nameModel;

const {
  groupId: addressGroupId,
  formFields: { street1, street2, city, state, zip, country },
} = addressModel;

const {
  formFields: { birthDate },
} = birthDateModel;

const {
  formFields: { phoneNumber },
} = phoneNumberModel;

const {
  formFields: { ssn, confirmSsn },
} = ssnModel;

const {
  formFields: { emailAddress },
} = emailAddressModel;

/* Name components */
export function FirstName(props: PiiInputProps) {
  return (
    <Input
      label={first.label}
      placeholder={first.placeholder}
      name={`${nameGroupId}.${first.name}`}
      qaSelector={QaDataCollectionModal.inputFirstName}
      {...props}
    />
  );
}

export function MiddleName(props: PiiInputProps) {
  return (
    <Input
      label={middle.label}
      placeholder={middle.placeholder}
      name={`${nameGroupId}.${middle.name}`}
      qaSelector={QaDataCollectionModal.inputMiddleName}
      {...props}
    />
  );
}

export function LastName(props: PiiInputProps) {
  return (
    <Input
      label={last.label}
      placeholder={last.placeholder}
      name={`${nameGroupId}.${last.name}`}
      qaSelector={QaDataCollectionModal.inputLastName}
      {...props}
    />
  );
}

export function NameSuffix(props: PiiSelectProps) {
  return (
    <Select
      label={suffix.label}
      placeholder={suffix.placeholder}
      name={`${nameGroupId}.${suffix.name}`}
      options={SUFFIX_OPTIONS}
      qaSelector={QaDataCollectionModal.dropdownSuffix}
      {...props}
    />
  );
}

/* Address components */
function BuildAddressLabel(address: LooseObjectType) {
  const { entries } = address;
  const addressLabel = buildAddress(address);
  return (
    <Flex className='gap8' justify='space-between'>
      {addressLabel}
      <If condition={entries > 1}>
        <Flex className='addressCount gap4'>
          <div>+ {entries} addresses</div>
          <LazySvg name='chevronDown' className='IconRightArrow' />
        </Flex>
      </If>
    </Flex>
  );
}

export function Street1Address(props: PiiAutoCompleteProps) {
  const dispatch = useAppDispatch();
  const autoCompleteRef = document.getElementById('addressAutoComplete');
  const { setFieldValue, validateForm } =
    useFormikContext<typeof homeAddressFormInitialValues>();
  const [options, setOptions] = useState<SmartyAddressType[]>([]);
  const [openDrodown, setOpenDrodown] = useState(false);

  const formatedOptions = useMemo(() => {
    return options?.map((opt, index) => {
      return {
        key: index,
        label: BuildAddressLabel(opt),
        value: buildAddress(opt),
      };
    });
  }, [options]);

  const debouncedSearch = useCallback(
    debounce(async (address: string) => {
      try {
        if (address) {
          const res = await dispatch(searchSmartyAddress({ search: address }));
          setOptions(res as any);
        } else {
          setOptions([]);
          setOpenDrodown(false);
        }
      } catch (error) {
        setOptions([]);
        logErrors(error);
      }
    }, 1000),
    [dispatch],
  );

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  // handle address select
  const handleSelect: OnSelectHandler = async (_value, selectedOption) => {
    try {
      const index = selectedOption.key;
      if (options.length) {
        const {
          entries,
          street_line: selectedStreet2,
          secondary,
          city: selectedCity,
          state: selectedState,
          zipcode,
        } = options[index];
        setFieldValue(addressGroupId, {
          [street1.name]: selectedStreet2,
          [street2.name]: secondary,
          [city.name]: selectedCity,
          [state.name]: selectedState,
          [zip.name]: zipcode,
          [country.name]: 'us',
        }).then(() => {
          validateForm();
        });

        if (entries > 1) {
          const res = await dispatch(
            searchSmartyAddress({
              search: `${selectedStreet2} ${secondary}`,
              selected: `${selectedStreet2} ${secondary} (${entries}) ${selectedCity} ${selectedState} ${zipcode}`,
            }),
          );
          setOptions(res);
          setOpenDrodown(true);
          if (autoCompleteRef) autoCompleteRef?.focus();
        } else {
          setOpenDrodown(false);
          if (autoCompleteRef) autoCompleteRef?.blur();
        }
      } else {
        setOpenDrodown(false);
      }
    } catch (error) {
      setOptions([]);
      logErrors(error);
    }
  };

  // handle address search
  const handleSearch = (address: string) => {
    if (!address) {
      setOptions([]);
      return;
    }
    debouncedSearch(address);
  };

  const handleFocus = () => {
    setOpenDrodown(true);
    if (options.length && !openDrodown) {
      setOpenDrodown(true);
    }
  };

  return (
    <AutoComplete
      id='addressAutoComplete'
      rootClassName='smartyAddress'
      onSelect={handleSelect}
      onSearch={handleSearch}
      options={formatedOptions}
      open={openDrodown}
      onBlur={() => setOpenDrodown(false)}
      onFocus={handleFocus}
      name={`${addressGroupId}.${street1.name}`}
      placeholder={street1.placeholder}
      label={street1.label}
      {...props}
      qaSelector={QaDataCollectionModal.inputAddress}
    />
  );
}

export function Street2Address(props: PiiInputProps) {
  return (
    <Input
      name={`${addressGroupId}.${street2.name}`}
      placeholder={street2.placeholder}
      label={street2.label}
      {...props}
      qaSelector={QaDataCollectionModal.inputSuite}
    />
  );
}

export function CityAddress(props: PiiInputProps) {
  return (
    <Input
      name={`${addressGroupId}.${city.name}`}
      placeholder={city.placeholder}
      label={city.label}
      qaSelector={QaDataCollectionModal.inputCity}
      {...props}
    />
  );
}

export function StateAddress(props: PiiSelectProps) {
  const [stateOptions, setStateOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    async function getUSStates() {
      const response = await fetchUSStates();
      if (response.length) {
        setStateOptions(response);
      }
    }
    getUSStates();
  }, []);

  const filterOption = (input: string, option?: DefaultOptionType) =>
    (option?.label?.toString() ?? '')
      .toLowerCase()
      .includes(input.toLowerCase());

  return (
    <Select
      showSearch
      label={state.label}
      placeholder={state.placeholder}
      name={`${addressGroupId}.${state.name}`}
      optionFilterProp='children'
      filterOption={filterOption}
      options={stateOptions}
      qaSelector={QaDataCollectionModal.dropdownState}
      {...props}
    />
  );
}

export function ZipAddress(props: PiiInputProps) {
  return (
    <Input
      name={`${addressGroupId}.${zip.name}`}
      label={zip.label}
      placeholder={zip.placeholder}
      maxLength={zip.length}
      qaSelector={QaDataCollectionModal.inputZip}
      {...props}
    />
  );
}

/* Birth date components */
export function BirthDate(props: PiiDatePickerProps) {
  const maxDate = dayjs().subtract(birthDate.minAge, 'y');
  const minDate = dayjs().subtract(birthDate.maxAge, 'y');

  return (
    <DatePicker
      format={birthDate.format}
      label={birthDate.label}
      placeholder={birthDate.placeholder}
      minDate={minDate}
      maxDate={maxDate}
      name={birthDate.name}
      qaSelector={QaDataCollectionModal.inputBirthdate}
      {...props}
    />
  );
}

/* Phone number components */
export const PhoneNumber = forwardRef<InputRef, PiiMaskedInputProps>(
  (props, ref) => {
    return (
      <InputMask
        ref={ref}
        placeholder={phoneNumber.placeholder}
        label={phoneNumber.label}
        name={phoneNumber.name}
        mask={phoneNumber.mask}
        qaSelector={QaDataCollectionModal.inputPhoneNumber}
        {...props}
      />
    );
  },
);

/* SSN components */
export function SSN(props: PiiPasswordProps) {
  return (
    <Password
      label={ssn.label}
      placeholder={ssn.placeholder}
      name={ssn.name}
      qaSelector={QaDataCollectionModal.inputSsn}
      tooltip='Social Security Number'
      {...props}
    />
  );
}

/* ConfirmSSN components */
export function ConfirmSSN(props: PiiPasswordProps) {
  return (
    <Password
      label={confirmSsn.label}
      placeholder={confirmSsn.placeholder}
      name={confirmSsn.name}
      tooltip='Social Security Number'
      {...props}
      qaSelector={QaDataCollectionModal.inputSSNConfirm}
    />
  );
}

/* Email address components */
export function EmailAddress(props: PiiInputProps) {
  return (
    <Input
      type={emailAddress.type}
      label={emailAddress.label}
      placeholder={emailAddress.placeholder}
      name={emailAddress.name}
      qaSelector={QaDataCollectionModal.inputEmail}
      {...props}
    />
  );
}
